import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  CartesianGrid
} from 'recharts';
import { AuthContext } from '../contexts/AuthContext';
import { Scorecard } from '../components/utility/Charts';
import {
  StoreDateRangeFilter,
  Loading,
  ToolTip
} from '../components/utility/Utility';
import { formatInTimeZone, toDate } from 'date-fns-tz';
import { addDays } from 'date-fns/addDays';
import {
  storeApiBoundsFromMatch,
  storeMatchRangeLastNCalendarDays,
} from '../utils/storeDateRange';
import {
  DEFAULT_RANGE_LABEL,
  RANGE_PRESETS,
  applyUrlFiltersToSeed,
  parseViewState,
  resolveRangeFromUrl,
  serializeDetailViewState
} from '../utils/productAnalysisUrlState';
import { formatBriefDate } from '../components/ProductBriefsComponents.jsx';
import {
  MultiSelectFilter,
  LocationMultiSelectFilter,
  filtersAllowAnalytics,
  buildFilterQueryParams,
  mergeFiltersAfterFilterCandidates,
  snapshotFacetLists
} from './ProductAnalysis.jsx';
import { ReactComponent as ImagePlaceholderIcon } from '../assets/images/icons/image-placeholder.svg';
import { ReactComponent as RobotIcon } from '../assets/images/icons/robot.svg';
import '../assets/css/components/program.css';
import '../assets/css/charts.css';
import '../assets/css/product-briefs.css';

axios.defaults.withCredentials = true;

function swatchHexLabel(hex, fallbackName) {
  if (hex && /^#/.test(String(hex).trim())) return String(hex).trim();
  if (fallbackName) return String(fallbackName);
  return '—';
}

function isPrimaryPaletteSwatch(entry, index) {
  const r = Number(entry?.rank);
  if (Number.isFinite(r) && r === 1) return true;
  if (!Number.isFinite(r) && index === 0) return true;
  return false;
}

function isAiAnalysisSuccess(status) {
  return String(status || '').toLowerCase() === 'success';
}

function aiAnalysisIndicatorTooltip(status) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'success') return 'AI analysis completed successfully.';
  if (normalized === 'error' || normalized === 'failed') {
    return 'The analysis failed. Try running it again and make sure the product has at least one image.';
  }
  if (normalized === 'pending') return 'AI analysis is in progress.';
  return 'Run AI analysis for the first time to unlock the color palette and other product insights.';
}

function normalizeProductImages(product) {
  const fromList = Array.isArray(product?.images)
    ? product.images.filter((img) => img?.src)
    : [];
  if (fromList.length > 0) return fromList;
  if (product?.image?.src) return [product.image];
  return [];
}

function ProductHeroLightbox({ images, productTitle, initialIndex, onClose }) {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex, images]);

  const current = images[index];
  const hasMultiple = images.length > 1;
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;

  const goPrev = () => {
    if (hasPrev) setIndex((i) => i - 1);
  };
  const goNext = () => {
    if (hasNext) setIndex((i) => i + 1);
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(images.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, images.length]);

  if (!current?.src) return null;

  return (
    <div className="product-brief-mockup-lightbox product-hero-lightbox" role="dialog" aria-modal="true">
      <button
        type="button"
        className="product-brief-mockup-lightbox__backdrop"
        onClick={onClose}
        aria-label="Close image"
      />
      <div className="product-brief-mockup-lightbox__panel product-hero-lightbox__panel">
        <button
          type="button"
          className="product-brief-mockup-lightbox__close"
          onClick={onClose}
          aria-label="Close image"
        >
          ×
        </button>
        <div className="product-hero-lightbox__stage">
          {hasMultiple ? (
            hasPrev ? (
              <button
                type="button"
                className="product-hero-lightbox__nav product-hero-lightbox__nav--prev"
                onClick={goPrev}
                aria-label="Previous image"
              >
                ‹
              </button>
            ) : (
              <span className="product-hero-lightbox__nav-spacer" aria-hidden />
            )
          ) : null}
          <img
            className="product-brief-mockup-lightbox__img product-hero-lightbox__img"
            src={current.src}
            alt={current.alt || productTitle || 'Product'}
          />
          {hasMultiple ? (
            hasNext ? (
              <button
                type="button"
                className="product-hero-lightbox__nav product-hero-lightbox__nav--next"
                onClick={goNext}
                aria-label="Next image"
              >
                ›
              </button>
            ) : (
              <span className="product-hero-lightbox__nav-spacer" aria-hidden />
            )
          ) : null}
        </div>
        {hasMultiple ? (
          <div className="product-hero-lightbox__counter" aria-live="polite">
            {index + 1} / {images.length}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ProductAiAnalysisIndicator({ status }) {
  const success = isAiAnalysisSuccess(status);
  const tooltip = aiAnalysisIndicatorTooltip(status);
  return (
    <div className="product-analysis-detail__ai-status" aria-label={tooltip}>
      <span className="product-analysis-detail__ai-status-label">AI Analysis</span>
      <ToolTip
        className="product-analysis-detail__ai-status-tooltip"
        width={200}
        message={tooltip}
        trigger={
          <span
            className={`product-analysis-detail__ai-status-ring${
              success ? ' product-analysis-detail__ai-status-ring--success' : ' product-analysis-detail__ai-status-ring--error'
            }`}
          >
            <RobotIcon className="product-analysis-detail__ai-status-icon" aria-hidden="true" />
          </span>
        }
      />
    </div>
  );
}

// Per-product-type exponential decay rate used to taper projected daily units
// during an open stockout. Case-insensitive substring match against the
// product type; falls back to the default if none match. Numbers come straight
// from the opportunity-cost chart spec.
const PRODUCT_TYPE_LAMBDA_MAP = [
  { match: /tee/i, lambda: 0.020 },
  { match: /hoodie/i, lambda: 0.014 },
  { match: /tank/i, lambda: 0.025 },
  { match: /(cap|hat)/i, lambda: 0.018 }
];
const DEFAULT_PRODUCT_TYPE_LAMBDA = 0.020;

function decayLambdaForProductType(productType) {
  const s = String(productType || '');
  for (const entry of PRODUCT_TYPE_LAMBDA_MAP) {
    if (entry.match.test(s)) return entry.lambda;
  }
  return DEFAULT_PRODUCT_TYPE_LAMBDA;
}

// Walk YYYY-MM-DD strings in `timezone` from start to end inclusive. Mirrors
// the bucketing used by the per-variant daily aggregation on the server so
// chart rows line up exactly with `daily_sales_by_variant`.
function buildDayTimeline(start, end, timezone) {
  const rows = [];
  let ymd = formatInTimeZone(start, timezone, 'yyyy-MM-dd');
  const endYmd = formatInTimeZone(end, timezone, 'yyyy-MM-dd');
  while (ymd <= endYmd) {
    rows.push(ymd);
    const next = addDays(toDate(`${ymd}T12:00:00`, { timeZone: timezone }), 1);
    ymd = formatInTimeZone(next, timezone, 'yyyy-MM-dd');
  }
  return rows;
}

function emptyOpportunityProjection(source = 'live') {
  return {
    chartRows: [],
    oppCost: 0,
    actualRevenue: 0,
    projectedRevenue: 0,
    lostUnits: 0,
    anyDegradedConfidence: false,
    stockoutDayBySelected: new Map(),
    source
  };
}

/** Scorecard + chart from stored product_opportunity_days (location shares already summed). */
function projectionFromStoredOpportunityDays({
  timezone,
  match,
  variantsByDay,
  selectedVariantIds,
  opportunityDays
}) {
  const empty = emptyOpportunityProjection('stored');
  if (!timezone || !match?.start || !match?.end) return empty;
  const timeline = buildDayTimeline(match.start, match.end, timezone);
  if (!timeline.length) return empty;

  const extraByDayVariant = new Map();
  for (const r of opportunityDays || []) {
    const day = String(r.date || '');
    const vid = String(r.shopify_variant_id || '');
    if (!day || !vid) continue;
    if (!extraByDayVariant.has(day)) extraByDayVariant.set(day, new Map());
    const existing = extraByDayVariant.get(day).get(vid) || { cost: 0, units: 0 };
    extraByDayVariant.get(day).set(vid, {
      cost: existing.cost + Number(r.opportunity_cost || 0),
      units: existing.units + Number(r.lost_units || 0)
    });
  }

  const chartRows = [];
  let oppCost = 0;
  let actualRevenue = 0;
  let projectedRevenue = 0;
  let lostUnits = 0;
  const stockoutDayBySelected = new Map();

  for (const day of timeline) {
    let actualNet = 0;
    let actualUnits = 0;
    let extraCost = 0;
    let extraUnits = 0;
    for (const vid of selectedVariantIds) {
      const cell = variantsByDay.get(day)?.get(vid);
      actualNet += cell?.net || 0;
      actualUnits += cell?.qty || 0;
      const extra = extraByDayVariant.get(day)?.get(vid);
      if (extra) {
        extraCost += extra.cost;
        extraUnits += extra.units;
      }
    }
    const projNet = actualNet + extraCost;
    chartRows.push({ Date: day, Actual: actualNet, Projected: projNet });
    actualRevenue += actualNet;
    projectedRevenue += projNet;
    const oosToday = extraCost > 0 || extraUnits > 0;
    if (oosToday) {
      oppCost += extraCost;
      lostUnits += extraUnits;
    }
    stockoutDayBySelected.set(day, oosToday);
  }

  return {
    chartRows,
    oppCost,
    actualRevenue,
    projectedRevenue,
    lostUnits,
    anyDegradedConfidence: false,
    stockoutDayBySelected,
    source: 'stored'
  };
}

const LOCKED_PALETTE_TOOLTIP = 'Complete AI analysis to view.';
const LOCKED_PALETTE_PLACEHOLDER_COLORS = ['#ef4444', '#22c55e', '#3b82f6', '#eab308'];

/** Read-only palette swatches — click hex to copy when AI analysis succeeded. */
function ProductAiSwatches({ colorPalette, aiAnalysisStatus }) {
  const analysisSuccess = isAiAnalysisSuccess(aiAnalysisStatus);
  const palette = analysisSuccess ? (colorPalette || []).slice(0, 5) : [];
  const [copiedHex, setCopiedHex] = useState('');
  const copiedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
    };
  }, []);

  if (!analysisSuccess) {
    return (
      <div className="product-brief-swatches product-analysis-detail__swatches--locked">
        <span className="product-briefs__muted" style={{ fontSize: '0.7rem' }}>
          Color Palette
        </span>
        {LOCKED_PALETTE_PLACEHOLDER_COLORS.map((hex, i) => (
          <ToolTip
            key={i}
            className="product-brief-swatch-tooltip"
            message={LOCKED_PALETTE_TOOLTIP}
            trigger={
              <span
                className={`product-brief-swatch product-analysis-detail__swatch-hit product-analysis-detail__swatch-hit--locked${
                  i === 0 ? ' product-brief-swatch--base' : ''
                }`}
                style={{ background: hex }}
                aria-hidden="true"
              />
            }
          />
        ))}
      </div>
    );
  }

  if (!palette.length) return null;

  const copyHex = async (hex) => {
    const value = swatchHexLabel(hex, '');
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedHex(value);
      if (copiedTimerRef.current) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopiedHex(''), 1200);
    } catch (_) {
      // Clipboard can fail in some browser contexts; no-op to keep UI simple.
    }
  };

  return (
    <div className="product-brief-swatches product-analysis-detail__swatches--copyable">
      <span className="product-briefs__muted" style={{ fontSize: '0.7rem' }}>
        Color Palette
      </span>
      {palette.map((c, i) => (
        <button
          key={i}
          type="button"
          className="product-analysis-detail__swatch-copy-wrap"
          onClick={() => copyHex(c.hex)}
          aria-label={
            isPrimaryPaletteSwatch(c, i)
              ? `Copy primary (rank 1) color hex ${swatchHexLabel(c.hex, c.name)}`
              : `Copy palette color ${i + 1} hex ${swatchHexLabel(c.hex, c.name)}`
          }
        >
          <ToolTip
            className="product-brief-swatch-tooltip"
            message={copiedHex && copiedHex === swatchHexLabel(c.hex, c.name) ? 'Copied!' : swatchHexLabel(c.hex, c.name)}
            trigger={
              <span
                className={`product-brief-swatch product-analysis-detail__swatch-hit${
                  isPrimaryPaletteSwatch(c, i) ? ' product-brief-swatch--base' : ''
                }`}
                style={{ background: c.hex && /^#/.test(c.hex) ? c.hex : '#ddd' }}
              />
            }
          />
        </button>
      ))}
    </div>
  );
}

export default function ProductAnalysisProductDetail() {
  const { shopifyProductId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStateRef = useRef(null);
  if (urlStateRef.current === null) urlStateRef.current = parseViewState(searchParams);
  const backTo = location.state?.from || '/app/product-analysis/analytics';
  const backLabel = location.state?.fromLabel || 'Analytics';
  const { getJwtToken, userProfile } = useContext(AuthContext);
  const timezone = userProfile?.store?.settings?.timezone;
  const [heroLightboxOpen, setHeroLightboxOpen] = useState(false);

  const [activeRange, setActiveRange] = useState(DEFAULT_RANGE_LABEL);
  const start = new Date();
  start.setDate(start.getDate() - 7);
  const end = new Date();

  end.setDate(end.getDate() - 1);
  const [match, setMatch] = useState({ start, end });
  const seededMatchForTz = useRef(null);
  const filtersSeededRef = useRef(false);
  const facetsRef = useRef(null);
  const matchKeyRef = useRef('');
  const [filterFacetsReady, setFilterFacetsReady] = useState(false);

  const [facets, setFacets] = useState({
    // titles: [{id, name}] — parallels ProductAnalysis.jsx; not surfaced as a filter UI here.
    titles: [],
    collections: [],
    product_types: [],
    tags: [],
    locations: [],
    sales_channels: []
  });
  const [filters, setFilters] = useState({
    product_ids: [],
    collection_ids: [],
    product_types: [],
    tags: [],
    location_ids: [],
    sales_channels: []
  });
  facetsRef.current = facets;

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [patchBusy, setPatchBusy] = useState(false);
  const [patchMsg, setPatchMsg] = useState('');
  const [masterSkuEditing, setMasterSkuEditing] = useState(false);
  const [masterSkuDraft, setMasterSkuDraft] = useState('');
  const [variantEdits, setVariantEdits] = useState({});
  const [pricesEditing, setPricesEditing] = useState(false);
  // Records the (variantId, field) cell that triggered edit mode so we can
  // hand it focus on the next render via autoFocus.
  const [priceFocusKey, setPriceFocusKey] = useState(null);
  // Ref attached to the variant table card; used as the boundary for the
  // click-outside handler that exits edit mode while preserving pending
  // changes (the Save/Reset buttons stay visible until commit or reset).
  const variantTableRef = useRef(null);

  const numberFormat = useMemo(() => new Intl.NumberFormat('en-US'), []);
  const dollarFormat = useMemo(
    () => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }),
    []
  );

  const analysisQueryParams = useMemo(() => {
    if (!timezone) return null;
    const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    if (!filtersAllowAnalytics(facets, filters)) return null;
    return {
      start: matchStart,
      end: matchEnd,
      timezone,
      ...buildFilterQueryParams(facets, filters)
    };
  }, [match, timezone, filters, facets]);

  // Stable JSON key used as the data-fetch dependency so unrelated re-renders
  // (e.g. AuthContext value churn, new Date() refs from `setMatch`) don't trigger a duplicate request.
  const analysisQueryKey = useMemo(
    () => (analysisQueryParams ? JSON.stringify(analysisQueryParams) : null),
    [analysisQueryParams]
  );

  // Stable key for narrowed filters that become URL params (channels + locations on this page).
  const filtersSelectionKey = useMemo(
    () => JSON.stringify(buildFilterQueryParams(facets, filters)),
    [facets, filters]
  );

  // Stable numeric key for the calendar range; same rationale as on ProductAnalysis.jsx.
  const matchKey = useMemo(
    () => `${match.start ? match.start.getTime() : ''}-${match.end ? match.end.getTime() : ''}`,
    [match]
  );
  matchKeyRef.current = matchKey;

  useEffect(() => {
    if (!timezone || seededMatchForTz.current === timezone) return;
    seededMatchForTz.current = timezone;
    const fromUrl = resolveRangeFromUrl(timezone, urlStateRef.current.range);
    if (fromUrl) {
      setMatch(fromUrl.match);
      setActiveRange(fromUrl.activeRange);
      return;
    }
    setMatch(storeMatchRangeLastNCalendarDays(timezone, 7));
    setActiveRange(DEFAULT_RANGE_LABEL);
  }, [timezone]);

  useEffect(() => {
    if (!timezone) return;
    const { start: matchStart, end: matchEnd } = storeApiBoundsFromMatch(timezone, match.start, match.end);
    const capturedMatchKey = matchKey;
    let cancelled = false;
    const load = async () => {
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          'https://api.amplifyloyalty.com/app/product-analysis/filter-candidates',
          { start: matchStart, end: matchEnd, timezone, include_locations: true },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;
        if (capturedMatchKey !== matchKeyRef.current) return;
        const data = res.data || {};
        const locs = Array.isArray(data.locations) ? data.locations : [];
        const chans = Array.isArray(data.sales_channels) ? data.sales_channels : [];
        const incomingTitles = Array.isArray(data.titles) ? data.titles : [];
        const titlesIdName = incomingTitles
          .map((t) => ({ id: Number(t?.id), name: String(t?.title ?? t?.name ?? '') }))
          .filter((t) => Number.isFinite(t.id) && t.id > 0);
        const incomingCollections = Array.isArray(data.collections) ? data.collections : [];
        const collectionsIdName = incomingCollections
          .map((c) => ({ id: Number(c?.id), name: String(c?.title ?? c?.name ?? '') }))
          .filter((c) => Number.isFinite(c.id) && c.id > 0);
        const snapshotFacets = snapshotFacetLists(facetsRef.current);
        const nextFacets = {
          titles: titlesIdName,
          collections: collectionsIdName,
          product_types: data.product_types || [],
          tags: data.tags || [],
          locations: locs,
          sales_channels: chans
        };
        setFacets(nextFacets);
        setFilterFacetsReady(true);
        if (!filtersSeededRef.current) {
          filtersSeededRef.current = true;
          const seed = {
            product_ids: titlesIdName.map((t) => String(t.id)),
            collection_ids: collectionsIdName.map((c) => String(c.id)),
            product_types: [...(data.product_types || [])],
            tags: [...(data.tags || [])],
            location_ids: locs.map((l) => String(l.id)),
            sales_channels: [...chans]
          };
          const urlFilters = urlStateRef.current.filters;
          urlStateRef.current.filters = null;
          setFilters(applyUrlFiltersToSeed(seed, urlFilters, nextFacets));
        } else {
          setFilters((prev) => {
            const next = mergeFiltersAfterFilterCandidates(prev, snapshotFacets, nextFacets);
            try {
              if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
            } catch {
              /* ignore */
            }
            return next;
          });
        }
      } catch (err) {
        if (!cancelled) console.log(err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchKey, timezone, getJwtToken]);

  // Mirror date range + channel/location filters into the URL once facets are seeded.
  useEffect(() => {
    if (!timezone || !filterFacetsReady || !filtersSeededRef.current) return;
    const next = serializeDetailViewState({
      narrowedFilters: buildFilterQueryParams(facets, filters),
      activeRange,
      match,
      timezone
    }).toString();
    if (next === window.location.search.replace(/^\?/, '')) return;
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersSelectionKey, matchKey, activeRange, filterFacetsReady, timezone, setSearchParams]);

  useEffect(() => {
    const idNum = Number(shopifyProductId);
    if (!shopifyProductId || !Number.isFinite(idNum) || idNum <= 0) {
      setError('Invalid product id.');
      setLoading(false);
      return;
    }
    if (!analysisQueryParams) return;

    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const token = await getJwtToken();
        const res = await axios.post(
          `https://api.amplifyloyalty.com/app/product-analysis/analytics/product/${encodeURIComponent(shopifyProductId)}`,
          analysisQueryParams,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDetail(res.data);
      } catch (err) {
        const msg =
          err?.response?.status === 404
            ? 'Product not found or does not match your filters.'
            : 'Failed to load product detail.';
        setError(msg);
        setDetail(null);
      } finally {
        setLoading(false);
      }
    };
    run();
    // `analysisQueryKey` already encodes start/end/timezone/filters so the previous
    // match.start / match.end / analysisQueryParams (object ref) deps were redundant
    // and caused duplicate fetches on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopifyProductId, analysisQueryKey, getJwtToken]);

  const patchFilters = (patch) => {
    setFilters((p) => ({ ...p, ...patch }));
  };

  const product = detail?.product;
  const productImages = useMemo(() => normalizeProductImages(product), [product]);
  const openHeroLightbox = () => setHeroLightboxOpen(true);
  const variants = useMemo(
    () => (Array.isArray(detail?.variants) ? detail.variants : []),
    [detail?.variants]
  );

  const [selectedVariantIds, setSelectedVariantIds] = useState(() => new Set());

  // Default every variant ON whenever the product or its variant set changes.
  // We can't memoize this with the variants memo because the toggle state is
  // user-mutable; this effect just seeds the initial selection.
  useEffect(() => {
    if (!variants.length) {
      setSelectedVariantIds(new Set());
      return;
    }
    setSelectedVariantIds(new Set(variants.map((v) => String(v.shopify_variant_id))));
  }, [shopifyProductId, variants]);

  const decayLambda = useMemo(
    () => decayLambdaForProductType(detail?.product?.product_type),
    [detail?.product?.product_type]
  );

  const chartLookbackStart = useMemo(() => {
    const iso = detail?.chart_lookback_start;
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d : null;
  }, [detail?.chart_lookback_start]);

  // YYYY-MM-DD -> variant_id -> { qty, net }. Pre-startDate slice is needed
  // for weighted velocity, the visible-range slice drives the actual line.
  // Sum across all sales channels for each date/variant combination.
  const variantsByDay = useMemo(() => {
    const rows = Array.isArray(detail?.daily_sales_by_variant) ? detail.daily_sales_by_variant : [];
    const m = new Map();
    for (const r of rows) {
      const d = r.date;
      const vid = String(r.shopify_variant_id);
      if (!m.has(d)) m.set(d, new Map());
      const existing = m.get(d).get(vid) || { qty: 0, net: 0 };
      m.get(d).set(vid, { 
        qty: existing.qty + Number(r.qty || 0), 
        net: existing.net + Number(r.net || 0) 
      });
    }
    return m;
  }, [detail?.daily_sales_by_variant]);

  // variant_id -> sorted list of { start: Date, end: Date|null, open: boolean }.
  // `end: null` represents an open-ended window still active at chart_end.
  const stockoutWindowsByVariant = useMemo(() => {
    const rows = Array.isArray(detail?.variant_stockout_windows) ? detail.variant_stockout_windows : [];
    const m = new Map();
    for (const r of rows) {
      const vid = String(r.shopify_variant_id);
      const start = new Date(r.started_at);
      if (!Number.isFinite(start.getTime())) continue;
      const end = r.open ? null : new Date(r.ended_at);
      if (end && !Number.isFinite(end.getTime())) continue;
      if (!m.has(vid)) m.set(vid, []);
      m.get(vid).push({ start, end, open: Boolean(r.open) });
    }
    for (const list of m.values()) list.sort((a, b) => a.start - b.start);
    return m;
  }, [detail?.variant_stockout_windows]);

  // Chip dot indicator: did any of the variant's windows overlap the visible
  // chart range? Used only for the dot on the toggle - the actual chart math
  // happens in projectionByVariant below.
  const variantHadStockoutInWindow = useMemo(() => {
    const m = new Map();
    if (!stockoutWindowsByVariant.size) return m;
    const chartStartMs = match.start ? match.start.getTime() : null;
    const chartEndMs = match.end ? match.end.getTime() : null;
    if (chartStartMs == null || chartEndMs == null) return m;
    for (const [vid, list] of stockoutWindowsByVariant) {
      const hit = list.some((w) => {
        const wStart = w.start.getTime();
        const wEnd = w.end ? w.end.getTime() : Number.POSITIVE_INFINITY;
        return wEnd >= chartStartMs && wStart <= chartEndMs;
      });
      m.set(vid, hit);
    }
    return m;
  }, [stockoutWindowsByVariant, match.start, match.end]);

  // Per-variant whole-day OOS count clipped to the visible chart range. Uses
  // calendar-day buckets in the store timezone so a window that spans only a
  // few hours still counts as 1 day (matches how stockout windows are shown
  // elsewhere). Drives the new "OOS days" column on the variant table.
  const variantOosDaysInWindow = useMemo(() => {
    const m = new Map();
    if (!stockoutWindowsByVariant.size || !timezone) return m;
    if (!match.start || !match.end) return m;
    const chartStartMs = match.start.getTime();
    const chartEndMs = match.end.getTime();
    for (const [vid, list] of stockoutWindowsByVariant) {
      const days = new Set();
      for (const w of list) {
        const wStartMs = w.start.getTime();
        const wEndMs = w.end ? w.end.getTime() : chartEndMs;
        if (wEndMs < chartStartMs || wStartMs > chartEndMs) continue;
        const clampedStart = new Date(Math.max(wStartMs, chartStartMs));
        const clampedEnd = new Date(Math.min(wEndMs, chartEndMs));
        let ymd = formatInTimeZone(clampedStart, timezone, 'yyyy-MM-dd');
        const endYmd = formatInTimeZone(clampedEnd, timezone, 'yyyy-MM-dd');
        while (ymd <= endYmd) {
          days.add(ymd);
          const next = addDays(toDate(`${ymd}T12:00:00`, { timeZone: timezone }), 1);
          ymd = formatInTimeZone(next, timezone, 'yyyy-MM-dd');
        }
      }
      m.set(vid, days.size);
    }
    return m;
  }, [stockoutWindowsByVariant, match.start, match.end, timezone]);

  const rangeRevenue = variants.reduce((s, v) => s + Number(v.revenue || 0), 0);
  const variantSummaryRow = useMemo(() => {
    const sameValueOrDash = (values) => {
      if (!values.length) return '';
      const first = values[0];
      return values.every((v) => v === first) ? first : '';
    };
    const priceValues = variants.map((v) => {
      const id = String(v.shopify_variant_id);
      return variantEdits[id]?.price ?? (v.price != null ? String(v.price) : '');
    });
    const compareValues = variants.map((v) => {
      const id = String(v.shopify_variant_id);
      return variantEdits[id]?.compare ?? (v.compare_at_price != null ? String(v.compare_at_price) : '');
    });
    const totalQty = variants.reduce((s, v) => s + Number(v.qty_sold || 0), 0);
    
    // Calculate number of days in the selected time period
    let numberOfDays = 1;
    if (match.start && match.end && timezone) {
      const startYmd = formatInTimeZone(match.start, timezone, 'yyyy-MM-dd');
      const endYmd = formatInTimeZone(match.end, timezone, 'yyyy-MM-dd');
      const timeline = buildDayTimeline(match.start, match.end, timezone);
      numberOfDays = Math.max(1, timeline.length);
    }
    
    const selloutTimes = variants
      .map((v) => {
        const d = v.estimated_sellout_date ? new Date(v.estimated_sellout_date) : null;
        return d && Number.isFinite(d.getTime()) ? d.getTime() : null;
      })
      .filter((x) => x != null);
    return {
      price: sameValueOrDash(priceValues),
      compare: sameValueOrDash(compareValues),
      qty: totalQty,
      revenue: rangeRevenue,
      avgDaily: numberOfDays > 0 ? totalQty / numberOfDays : 0,
      oosDays: variants.reduce((s, v) => s + Number(variantOosDaysInWindow.get(String(v.shopify_variant_id)) || 0), 0),
      percentOfProduct: variants.length && rangeRevenue > 0 ? 1 : 0,
      estimatedSelloutDate: selloutTimes.length
        ? new Date(selloutTimes.reduce((s, x) => s + x, 0) / selloutTimes.length)
        : null,
      inventory: variants.reduce((s, v) => s + Number(v.inventory_current || 0), 0)
    };
  }, [rangeRevenue, variantEdits, variantOosDaysInWindow, variants, match.start, match.end, timezone]);

  useEffect(() => {
    const m = {};
    for (const v of variants) {
      const id = String(v.shopify_variant_id);
      m[id] = {
        price: v.price != null && v.price !== '' ? String(v.price) : '',
        compare: v.compare_at_price != null && v.compare_at_price !== '' ? String(v.compare_at_price) : ''
      };
    }
    setVariantEdits(m);
    // Whenever the upstream variants change (new product, refetch, or post-save
    // refresh) we drop edit mode + any pending focus.
    setPricesEditing(false);
    setPriceFocusKey(null);
  }, [variants]);

  // Truthy when any variantEdits row differs from its source variant. Drives
  // the visibility of the Save / Reset buttons under the table.
  const pricesDirty = useMemo(() => {
    if (!variants.length) return false;
    for (const v of variants) {
      const id = String(v.shopify_variant_id);
      const row = variantEdits[id];
      if (!row) continue;
      const origPrice = v.price != null ? String(v.price) : '';
      const origCompare = v.compare_at_price != null ? String(v.compare_at_price) : '';
      if (row.price !== origPrice) return true;
      if (row.compare !== origCompare) return true;
    }
    return false;
  }, [variantEdits, variants]);

  // Exit edit mode on click-outside-the-card. We intentionally don't reset
  // variantEdits here so any pending edits stay visible (via the display
  // cells) along with the Save / Reset buttons until the user commits or
  // resets explicitly.
  useEffect(() => {
    if (!pricesEditing) return undefined;
    const handlePointerDown = (event) => {
      const root = variantTableRef.current;
      if (!root) return;
      if (root.contains(event.target)) return;
      setPricesEditing(false);
      setPriceFocusKey(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [pricesEditing]);

  // Heart of the opportunity-cost chart. Returns the visible-range chart rows
  // (`{ Date, Actual, Projected }`), the four scorecard totals, the per-day
  // "any selected variant OOS" mask, and a confidence flag indicating whether
  // any selected variant had partial in-stock history for its velocity blend.
  //
  // Key invariants from the spec:
  //   - On in-stock days `Projected == Actual` so the lines coincide exactly.
  //   - For stockout windows that begin before the visible range, we still use
  //     the window's true `started_at` for both velocity lookback and decay,
  //     which is why we pull `chart_lookback_start` (>= 30d before chart start)
  //     in the daily-sales aggregation.
  //   - Per-variant projected units use weighted velocity (7/14/30 windows
  //     ending at window.start, in-stock days only, >=3 in-stock days each)
  //     multiplied by `exp(-lambda * daysSinceWindowStart)`.
  const liveProjectionByVariant = useMemo(() => {
    const empty = emptyOpportunityProjection('live');
    if (!variants.length || !detail || !timezone || !chartLookbackStart) return empty;
    if (!match.start || !match.end) return empty;

    const chartStart = match.start;
    const chartEnd = match.end;

    const timeline = buildDayTimeline(chartLookbackStart, chartEnd, timezone);
    if (!timeline.length) return empty;
    // Pre-bake local-midnight ms per timeline day so the per-variant inner
    // loops below stay tight (avoid re-running toDate per (day, window) pair).
    const dayMidnights = timeline.map((d) =>
      toDate(`${d}T00:00:00`, { timeZone: timezone }).getTime()
    );
    const dayEnds = dayMidnights.map((ms) => ms + 86400000 - 1);

    const chartStartYmd = formatInTimeZone(chartStart, timezone, 'yyyy-MM-dd');
    const chartEndYmd = formatInTimeZone(chartEnd, timezone, 'yyyy-MM-dd');
    const chartStartMs = chartStart.getTime();
    const chartEndMs = chartEnd.getTime();

    // Largest i such that dayMidnights[i] <= ts, or -1 if `ts` precedes the
    // timeline. Walks from the end because the typical caller (window.start)
    // sits at or near the right edge of the lookback span.
    const findDayIndex = (ts) => {
      for (let i = dayMidnights.length - 1; i >= 0; i--) {
        if (dayMidnights[i] <= ts) return i;
      }
      return -1;
    };

    const projectedUnitsByVariant = new Map();
    const projectedNetByVariant = new Map();
    const inStockByVariant = new Map();
    const confidenceByVariant = new Map();
    const confRank = { high: 3, medium: 2, low: 1, none: 0 };

    for (const v of variants) {
      const vid = String(v.shopify_variant_id);
      const price = Number(v.price || 0);

      const windows = stockoutWindowsByVariant.get(vid) || [];

      // Day-level in-stock mask for the full timeline.
      const inStockArr = new Array(timeline.length).fill(true);
      if (windows.length) {
        for (let i = 0; i < timeline.length; i++) {
          const dStart = dayMidnights[i];
          const dEnd = dayEnds[i];
          for (let j = 0; j < windows.length; j++) {
            const w = windows[j];
            const wStartMs = w.start.getTime();
            const wEndMs = w.end ? w.end.getTime() : Number.POSITIVE_INFINITY;
            if (wStartMs <= dEnd && wEndMs > dStart) {
              inStockArr[i] = false;
              break;
            }
          }
        }
      }
      inStockByVariant.set(vid, inStockArr);

      const units = new Array(timeline.length).fill(0);
      const net = new Array(timeline.length).fill(0);

      for (let i = 0; i < timeline.length; i++) {
        if (!inStockArr[i]) continue;
        const cell = variantsByDay.get(timeline[i])?.get(vid);
        if (cell) {
          units[i] = cell.qty || 0;
          net[i] = cell.net || 0;
        }
      }

      let bestConfidence = 'high';
      let touchedWindow = false;

      for (const w of windows) {
        const wStartMs = w.start.getTime();
        const wEndMs = w.end ? w.end.getTime() : Number.POSITIVE_INFINITY;
        // Skip windows entirely outside the visible chart range.
        if (wEndMs < chartStartMs || wStartMs > chartEndMs) continue;
        touchedWindow = true;

        const startIdx = findDayIndex(wStartMs);

        const computeAvg = (days) => {
          if (startIdx < 0) return null;
          let qtySum = 0;
          let inStockDays = 0;
          for (let back = 1; back <= days; back++) {
            const idx = startIdx - back;
            if (idx < 0) break;
            if (!inStockArr[idx]) continue;
            inStockDays += 1;
            const cell = variantsByDay.get(timeline[idx])?.get(vid);
            qtySum += cell?.qty || 0;
          }
          if (inStockDays < 3) return null;
          return qtySum / inStockDays;
        };

        const avg7 = computeAvg(7);
        const avg14 = computeAvg(14);
        const avg30 = computeAvg(30);

        const usable = [];
        if (avg7 != null) usable.push({ days: 7, weight: 0.5, value: avg7 });
        if (avg14 != null) usable.push({ days: 14, weight: 0.3, value: avg14 });
        if (avg30 != null) usable.push({ days: 30, weight: 0.2, value: avg30 });

        let conf = 'none';
        let velocity = null;
        if (usable.length === 3) conf = 'high';
        else if (usable.length === 2) conf = 'medium';
        else if (usable.length === 1) conf = 'low';

        if (usable.length) {
          const wsum = usable.reduce((s, x) => s + x.weight, 0);
          velocity = usable.reduce((s, x) => s + x.value * (x.weight / wsum), 0);
        }

        if (confRank[conf] < confRank[bestConfidence]) bestConfidence = conf;

        for (let i = 0; i < timeline.length; i++) {
          if (inStockArr[i]) continue;
          if (wStartMs > dayEnds[i]) continue;
          if (wEndMs <= dayMidnights[i]) continue;
          const daysSinceStart = startIdx >= 0 ? Math.max(0, i - startIdx) : 0;
          const projUnits = velocity != null
            ? velocity * Math.exp(-decayLambda * daysSinceStart)
            : 0;
          // Projected line on OOS days = actual sold + decayed unrealized
          // demand. Any POS/edge-case sales that snuck through during the
          // stockout still count toward "what we sold", and the decay term
          // models the additional revenue we'd have captured if the variant
          // were in stock. Difference vs actual is the opportunity cost.
          const cell = variantsByDay.get(timeline[i])?.get(vid);
          const actualUnitsToday = cell?.qty || 0;
          const actualNetToday = cell?.net || 0;
          units[i] = actualUnitsToday + projUnits;
          net[i] = actualNetToday + projUnits * price;
        }
      }

      projectedUnitsByVariant.set(vid, units);
      projectedNetByVariant.set(vid, net);
      confidenceByVariant.set(vid, touchedWindow ? bestConfidence : 'high');
    }

    const chartRows = [];
    let oppCost = 0;
    let actualRevenue = 0;
    let projectedRevenue = 0;
    let lostUnits = 0;
    const stockoutDayBySelected = new Map();

    for (let i = 0; i < timeline.length; i++) {
      const day = timeline[i];
      if (day < chartStartYmd || day > chartEndYmd) continue;
      let actualNet = 0;
      let projNet = 0;
      let actualUnits = 0;
      let projUnits = 0;
      let oosToday = false;
      for (const vid of selectedVariantIds) {
        const cell = variantsByDay.get(day)?.get(vid);
        const aQty = cell?.qty || 0;
        const aNet = cell?.net || 0;
        actualUnits += aQty;
        actualNet += aNet;
        const inStockArr = inStockByVariant.get(vid);
        const inStock = inStockArr ? inStockArr[i] : true;
        if (!inStock) oosToday = true;
        const pUnits = projectedUnitsByVariant.get(vid)?.[i] ?? 0;
        const pNet = projectedNetByVariant.get(vid)?.[i] ?? 0;
        projUnits += pUnits;
        projNet += pNet;
      }
      chartRows.push({ Date: day, Actual: actualNet, Projected: projNet });
      actualRevenue += actualNet;
      projectedRevenue += projNet;
      if (oosToday) {
        oppCost += Math.max(0, projNet - actualNet);
        lostUnits += Math.max(0, projUnits - actualUnits);
      }
      stockoutDayBySelected.set(day, oosToday);
    }

    let anyDegraded = false;
    for (const vid of selectedVariantIds) {
      const c = confidenceByVariant.get(vid);
      if (c === 'medium' || c === 'low' || c === 'none') {
        anyDegraded = true;
        break;
      }
    }

    return {
      chartRows,
      oppCost,
      actualRevenue,
      projectedRevenue,
      lostUnits,
      anyDegradedConfidence: anyDegraded,
      stockoutDayBySelected,
      source: 'live'
    };
  }, [
    variants,
    detail,
    timezone,
    chartLookbackStart,
    match.start,
    match.end,
    variantsByDay,
    stockoutWindowsByVariant,
    selectedVariantIds,
    decayLambda
  ]);

  const storedProjectionByVariant = useMemo(() => {
    const rows = Array.isArray(detail?.opportunity_days) ? detail.opportunity_days : [];
    if (!variants.length || !timezone) return emptyOpportunityProjection('stored');
    // Always build chart from sales data, even if no stored opportunity days
    return projectionFromStoredOpportunityDays({
      timezone,
      match,
      variantsByDay,
      selectedVariantIds,
      opportunityDays: rows
    });
  }, [
    detail?.opportunity_days,
    variants.length,
    timezone,
    match.start,
    match.end,
    variantsByDay,
    selectedVariantIds
  ]);

  const projectionByVariant = storedProjectionByVariant;

  const applyProductPatch = async (payload) => {
    setPatchBusy(true);
    setPatchMsg('');
    try {
      const token = await getJwtToken();
      const res = await axios.patch(
        `https://api.amplifyloyalty.com/app/product-analysis/analytics/product/${encodeURIComponent(shopifyProductId)}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      if (res.data?.product) {
        setDetail((prev) => (prev ? { ...prev, product: { ...prev.product, ...res.data.product } } : prev));
      }
      return res.data;
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || err?.message || 'Update failed.';
      setPatchMsg(typeof msg === 'string' ? msg : 'Update failed.');
      return null;
    } finally {
      setPatchBusy(false);
    }
  };

  const saveMasterSku = async () => {
    const trimmed = String(masterSkuDraft || '').trim();
    const ok = await applyProductPatch({ master_sku_override: trimmed || null });
    if (ok) setMasterSkuEditing(false);
  };

  useEffect(() => {
    if (!masterSkuEditing && product) {
      setMasterSkuDraft(product.master_sku || '');
    }
  }, [product?.master_sku, masterSkuEditing]);

  // Reverts every row back to its server-side values and exits edit mode.
  const resetVariantPrices = () => {
    const m = {};
    for (const v of variants) {
      const id = String(v.shopify_variant_id);
      m[id] = {
        price: v.price != null && v.price !== '' ? String(v.price) : '',
        compare: v.compare_at_price != null && v.compare_at_price !== '' ? String(v.compare_at_price) : ''
      };
    }
    setVariantEdits(m);
    setPricesEditing(false);
    setPriceFocusKey(null);
    setPatchMsg('');
  };

  // Validates every dirty row, batches the changed fields into a single PATCH
  // (`{ variants: [...] }`), and on success merges the new values back into
  // `detail.variants` so all derived UI (revenue % etc) stays consistent.
  const commitAllVariantPrices = async () => {
    if (!variants.length) return;
    const entries = [];
    for (const v of variants) {
      const vid = String(v.shopify_variant_id);
      const row = variantEdits[vid];
      if (!row) continue;
      const origPrice = v.price != null ? String(v.price) : '';
      const origCompare = v.compare_at_price != null ? String(v.compare_at_price) : '';
      if (row.price === origPrice && row.compare === origCompare) continue;

      const entry = { shopify_variant_id: vid };
      if (row.price !== origPrice) {
        if (row.price === '') {
          setPatchMsg(`Price cannot be empty (${v.variant_title || v.sku || vid}).`);
          return;
        }
        const priceNum = parseFloat(row.price);
        if (!Number.isFinite(priceNum) || priceNum < 0) {
          setPatchMsg(`Invalid price (${v.variant_title || v.sku || vid}).`);
          return;
        }
        entry.price = priceNum;
      }
      if (row.compare !== origCompare) {
        if (row.compare === '') entry.compare_at_price = null;
        else {
          const c = parseFloat(row.compare);
          if (!Number.isFinite(c) || c < 0) {
            setPatchMsg(`Invalid compare-at price (${v.variant_title || v.sku || vid}).`);
            return;
          }
          entry.compare_at_price = c;
        }
      }
      if (entry.price === undefined && entry.compare_at_price === undefined) continue;
      entries.push(entry);
    }
    if (!entries.length) {
      setPricesEditing(false);
      return;
    }

    const ok = await applyProductPatch({ variants: entries });
    if (ok) {
      const byVid = new Map(entries.map((e) => [String(e.shopify_variant_id), e]));
      setDetail((prev) => {
        if (!prev?.variants) return prev;
        return {
          ...prev,
          variants: prev.variants.map((x) => {
            const e = byVid.get(String(x.shopify_variant_id));
            if (!e) return x;
            return {
              ...x,
              ...(e.price !== undefined ? { price: e.price } : {}),
              ...(e.compare_at_price !== undefined ? { compare_at_price: e.compare_at_price } : {})
            };
          })
        };
      });
      setPricesEditing(false);
      setPriceFocusKey(null);
    }
  };

  if (!timezone) {
    return (
      <div className="product-analysis-scroll">
        <div className="dashboard dashboard--product-analysis float-element" style={{ padding: 20 }}>
          Store timezone is required for this view.
        </div>
      </div>
    );
  }

  return (
    <div className="product-analysis-scroll">
      <div className="dashboard dashboard--product-analysis">
        <div className="dashboard__filters float-element product-analysis-filters">
          <div className="product-analysis-detail__filters-toolbar">
            <Link
              to={backTo}
              className="product-briefs-filters__back product-analysis-detail__filters-back"
            >
              ← {backLabel}
            </Link>
            <div className="product-analysis-filters__cluster">
              <div className="date-filter promotions-table-filters__filter-wrapper">
                <StoreDateRangeFilter
                  active={activeRange}
                  setActive={setActiveRange}
                  timezone={timezone}
                  match={match}
                  options={[
                    ...RANGE_PRESETS.map((preset) => ({
                      text: preset.label,
                      action: () => setMatch(preset.resolve(timezone))
                    })),
                    {
                      text: 'Custom Range',
                      action: (start, end) => setMatch({ start, end })
                    }
                  ]}
                />
              </div>
              <MultiSelectFilter
                label="Sales channel"
                options={facets.sales_channels}
                value={filters.sales_channels}
                onChange={(sales_channels) => patchFilters({ sales_channels })}
                showSearch
                searchPlaceholder="Search sales channels"
              />
              <LocationMultiSelectFilter
                label="Locations"
                locations={facets.locations}
                value={filters.location_ids}
                onChange={(location_ids) => patchFilters({ location_ids })}
                showSearch
                searchPlaceholder="Search locations"
              />
            </div>
          </div>
        </div>

        {error && <div className="float-element" style={{ padding: 12 }}>{error}</div>}

        {!error && (
          <>
            <div className="float-element product-analysis-detail__hero">
              {productImages[0]?.src ? (
                <img
                  className="product-analysis-detail__hero-img product-analysis-detail__hero-img--clickable"
                  src={productImages[0].src}
                  alt={productImages[0].alt || product?.title || 'Product'}
                  width={120}
                  height={120}
                  role="button"
                  tabIndex={0}
                  onClick={openHeroLightbox}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openHeroLightbox();
                    }
                  }}
                />
              ) : (
                <div className="product-analysis-detail__hero-img product-analysis-detail__hero-img--placeholder" aria-hidden>
                  <ImagePlaceholderIcon className="product-analysis-detail__hero-img-placeholder-icon" />
                </div>
              )}
              <div className="product-analysis-detail__hero-main">
                <div className="product-analysis-detail__hero-title-row">
                  <h1 className="dashboard__title product-analysis-detail__hero-title">
                    {product?.title || product?.handle || `-`}
                  </h1>
                  <div className="product-analysis-detail__hero-meta">
                    <ProductAiAnalysisIndicator status={product?.ai_analysis_status} />
                    <label className="product-analysis-detail__status-label">
                    <span className="product-analysis-detail__status-label-text">Status</span>
                    <select
                      className="product-analysis-detail__status-select"
                      value={product?.status || 'ACTIVE'}
                      disabled={patchBusy || loading || !product}
                      onChange={(e) => applyProductPatch({ status: e.target.value })}
                      aria-label="Product status"
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="DRAFT">DRAFT</option>
                      <option value="ARCHIVED">ARCHIVED</option>
                    </select>
                  </label>
                  <div className="product-analysis-detail__master-sku-field">
                    <span className="product-analysis-detail__status-label-text">Master SKU</span>
                    <div className="product-analysis-detail__master-sku-control-wrap">
                      <div className="product-analysis-detail__master-sku-control">
                        {masterSkuEditing ? (
                          <input
                            type="text"
                            className="product-analysis-detail__master-sku-input"
                            value={masterSkuDraft}
                            disabled={patchBusy || loading || !product}
                            onChange={(e) => setMasterSkuDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveMasterSku();
                              }
                              if (e.key === 'Escape') {
                                e.preventDefault();
                                setMasterSkuDraft(product?.master_sku || '');
                                setMasterSkuEditing(false);
                              }
                            }}
                            aria-label="Master SKU"
                            autoFocus
                          />
                        ) : (
                          <button
                            type="button"
                            className="product-analysis-detail__master-sku-value"
                            disabled={patchBusy || loading || !product}
                            onClick={() => {
                              setMasterSkuDraft(product?.master_sku || '');
                              setMasterSkuEditing(true);
                            }}
                          >
                            {product?.master_sku || '—'}
                          </button>
                        )}
                        {masterSkuEditing ? (
                          <div className="product-analysis-detail__master-sku-edit-actions">
                            <button
                              type="button"
                              className="product-analysis-detail__master-sku-action product-analysis-detail__master-sku-action--ghost"
                              disabled={patchBusy || loading || !product}
                              onClick={() => {
                                setMasterSkuDraft(product?.master_sku || '');
                                setMasterSkuEditing(false);
                              }}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="product-analysis-detail__master-sku-action"
                              disabled={patchBusy || loading || !product}
                              onClick={saveMasterSku}
                            >
                              Save
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  </div>
                </div>
                <p className="product-briefs-filters__subtitle product-analysis-detail__hero-subtitle">
                  {product?.product_type || '—'} - Created{' '}
                  {product?.created_at ? formatBriefDate(product.created_at) : '—'}
                </p>
                {patchMsg ? <div className="product-analysis-detail__patch-msg">{patchMsg}</div> : null}
                {product && (
                  <div className="product-analysis-detail__hero-swatches">
                    <ProductAiSwatches
                      colorPalette={product.color_palette}
                      aiAnalysisStatus={product.ai_analysis_status}
                    />
                  </div>
                )}
              </div>
            </div>

            {heroLightboxOpen && productImages.length > 0 ? (
              <ProductHeroLightbox
                images={productImages}
                productTitle={product?.title}
                initialIndex={0}
                onClose={() => setHeroLightboxOpen(false)}
              />
            ) : null}

            <section
              className="float-element"
              style={{ width: '100%', padding: 20, boxSizing: 'border-box' }}
            >
                <div
                  className="product-analysis-scorecards"
                  style={{ marginBottom: 14, justifyContent: 'center', gap: 20 }}
                >
                  <Scorecard
                    className="dashboard__scorecard"
                    title="Actual revenue"
                    data={loading ? '-' : dollarFormat.format(projectionByVariant.actualRevenue)}
                  />
                  <Scorecard
                    className="dashboard__scorecard"
                    title="Projected revenue"
                    data={loading ? '-' : dollarFormat.format(projectionByVariant.projectedRevenue)}
                  />
                  <Scorecard
                    className="dashboard__scorecard"
                    title="Opportunity cost"
                    data={loading ? '-' : dollarFormat.format(projectionByVariant.oppCost)}
                  />
                  <Scorecard
                    className="dashboard__scorecard"
                    title="Lost units"
                    data={loading ? '-' : numberFormat.format(Math.round(projectionByVariant.lostUnits))}
                  />
                </div>

                {!loading && variants.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      marginBottom: 14
                    }}
                  >
                    {variants.map((v) => {
                      const vid = String(v.shopify_variant_id);
                      const isOn = selectedVariantIds.has(vid);
                      const hasStockoutDot = variantHadStockoutInWindow.get(vid) === true;
                      const label = v.variant_title || v.sku || vid;
                      return (
                        <button
                          type="button"
                          key={vid}
                          onClick={() => {
                            setSelectedVariantIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(vid)) next.delete(vid);
                              else next.add(vid);
                              return next;
                            });
                          }}
                          aria-pressed={isOn}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: '4px 10px',
                            borderRadius: 999,
                            border: `1px solid ${isOn ? '#3B82F6' : 'var(--border-gray, #c2c2c2)'}`,
                            background: isOn ? 'rgba(59, 130, 246, 0.12)' : '#fff',
                            color: isOn ? '#1d4ed8' : 'inherit',
                            fontSize: '0.8125rem',
                            cursor: 'pointer'
                          }}
                        >
                          <span>{label}</span>
                          {hasStockoutDot && (
                            <span
                              aria-hidden
                              title="Had a stockout in the visible window"
                              style={{
                                display: 'inline-block',
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                background: '#9B2C2C'
                              }}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div style={{ width: '100%', height: 320 }}>
                  {loading ? (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Loading className="product-analysis-table__loading" />
                    </div>
                  ) : projectionByVariant.chartRows.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={projectionByVariant.chartRows}
                        margin={{ top: 10, right: 20, left: 0, bottom: 5 }}
                      >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="Date"
                        tickFormatter={(value) => {
                          const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
                          if (!m) return String(value || '');
                          return `${Number(m[2])}/${Number(m[3])}`;
                        }}
                      />
                      <YAxis
                        tickFormatter={(v) =>
                          Math.abs(v) >= 1000
                            ? `$${Math.round(v / 1000)}k`
                            : dollarFormat.format(v)
                        }
                      />
                      <RechartsTooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length) return null;
                          const row = payload[0]?.payload || {};
                          const actual = Number(row.Actual || 0);
                          const projected = Number(row.Projected || 0);
                          const oppCost = Math.max(0, projected - actual);
                          const oos = projectionByVariant.stockoutDayBySelected.get(label);
                          return (
                            <div
                              style={{
                                background: '#fff',
                                border: '1px solid var(--border-gray, #c2c2c2)',
                                borderRadius: 6,
                                padding: '8px 10px',
                                fontSize: '0.8125rem',
                                lineHeight: 1.4,
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                              }}
                            >
                              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                {label}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span
                                  aria-hidden
                                  style={{ width: 10, height: 2, background: '#3B82F6', display: 'inline-block' }}
                                />
                                <span style={{ flex: 1 }}>Actual</span>
                                <span>{dollarFormat.format(actual)}</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span
                                  aria-hidden
                                  style={{
                                    width: 10,
                                    height: 2,
                                    background: 'repeating-linear-gradient(to right, #9B2C2C 0 4px, transparent 4px 7px)',
                                    display: 'inline-block'
                                  }}
                                />
                                <span style={{ flex: 1 }}>Projected</span>
                                <span>{dollarFormat.format(projected)}</span>
                              </div>
                              <div
                                style={{
                                  marginTop: 4,
                                  paddingTop: 4,
                                  borderTop: '1px solid var(--border-gray, #e0e0e0)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 6
                                }}
                              >
                                <span style={{ flex: 1, fontWeight: 600 }}>Opportunity cost</span>
                                <span style={{ fontWeight: 600 }}>{dollarFormat.format(oppCost)}</span>
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="Actual"
                        stroke="#3B82F6"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="Projected"
                        stroke="#9B2C2C"
                        strokeWidth={2}
                        strokeDasharray="6 4"
                        dot={false}
                      />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="product-analysis-table__empty-warning" style={{ paddingTop: 120, textAlign: 'center' }}>
                      No inventory opportunity data found for this product and date range.
                    </div>
                  )}
                </div>

                {!loading && projectionByVariant.anyDegradedConfidence && (
                  <p
                    className="product-briefs__muted"
                    style={{ marginTop: 10, fontSize: '0.85rem' }}
                  >
                    Heads up: one or more selected variants have limited in-stock history before the stockout (medium or low confidence). The projected line for those variants uses a partial weighted velocity blend.
                  </p>
                )}
              </section>

            <section className="product-analysis-table__container" style={{ marginTop: 20, width: '100%' }}>
              <div
                ref={variantTableRef}
                className="product-analysis-table product-analysis-table--product-detail"
                style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}
              >
                <div
                  className="line-graph__heading"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}
                >
                  <span>Variant Breakdown</span>
                  <div aria-hidden />
                </div>
                <div
                  className="product-analysis-table__table-container"
                  style={{ height: 'auto', maxHeight: 300 }}
                >
                  <div className="product-analysis-table__header">
                    <div className="product-analysis-table__heading product-analysis-table__heading--product">
                      <div className="product-analysis-table__heading-text">Variant</div>
                    </div>
                    <div className="product-analysis-table__metrics">
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">SKU</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Price</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Compare-at</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Qty</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Revenue</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Avg daily</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">OOS days</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">% of product</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Est. sell out</div>
                      </div>
                      <div className="product-analysis-table__heading">
                        <div className="product-analysis-table__heading-text">Inventory</div>
                      </div>
                    </div>
                  </div>
                  <div className="product-analysis-table__body">
                    {loading && (
                      <div
                        className="product-analysis-table__empty-warning"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}
                      >
                        <Loading className="product-analysis-table__loading" />
                      </div>
                    )}
                    {!loading && variants.length === 0 && (
                      <div className="product-analysis-table__empty-warning">No variants synced for this product.</div>
                    )}
                    {!loading && variants.map((v) => {
                      const key = `${v.shopify_variant_id}-${v.sku || ''}`;
                      return (
                        <div key={key} className="product-analysis-table__row" style={{ position: 'relative' }}>
                          <div
                            className="product-analysis-table__cell"
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            {v.variant_title || v.sku || v.shopify_variant_id}
                          </div>
                          <div className="product-analysis-table__metrics">
                            <div
                              className="product-analysis-table__cell"
                              style={{ color: '#666', fontSize: '0.8125rem' }}
                              title={v.sku || undefined}
                            >
                              {v.sku || '—'}
                            </div>
                            {pricesEditing ? (
                              <div className="product-analysis-table__cell product-analysis-detail__cell-input">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className="product-analysis-detail__table-input"
                                  disabled={patchBusy}
                                  autoFocus={priceFocusKey === `${v.shopify_variant_id}:price`}
                                  value={variantEdits[v.shopify_variant_id]?.price ?? ''}
                                  onChange={(e) =>
                                    setVariantEdits((prev) => ({
                                      ...prev,
                                      [v.shopify_variant_id]: {
                                        ...prev[v.shopify_variant_id],
                                        price: e.target.value
                                      }
                                    }))
                                  }
                                  aria-label={`Price for ${v.variant_title || v.sku || v.shopify_variant_id}`}
                                />
                              </div>
                            ) : (() => {
                              const row = variantEdits[v.shopify_variant_id];
                              const origPrice = v.price != null ? String(v.price) : '';
                              const editedRaw = row?.price;
                              const isDirty = editedRaw != null && editedRaw !== origPrice;
                              const raw = editedRaw != null ? editedRaw : origPrice;
                              const n = raw === '' ? null : parseFloat(raw);
                              const display = n == null || !Number.isFinite(n)
                                ? (raw === '' ? '—' : raw)
                                : dollarFormat.format(n);
                              return (
                                <div
                                  className="product-analysis-table__cell product-analysis-detail__cell-display"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setPriceFocusKey(`${v.shopify_variant_id}:price`);
                                    setPricesEditing(true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setPriceFocusKey(`${v.shopify_variant_id}:price`);
                                      setPricesEditing(true);
                                    }
                                  }}
                                  aria-label={`Edit price for ${v.variant_title || v.sku || v.shopify_variant_id}`}
                                  title="Click to edit prices"
                                  style={{ cursor: 'pointer', fontWeight: isDirty ? 700 : undefined }}
                                >
                                  {display}
                                </div>
                              );
                            })()}
                            {pricesEditing ? (
                              <div className="product-analysis-table__cell product-analysis-detail__cell-input">
                                <input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  className="product-analysis-detail__table-input"
                                  placeholder="—"
                                  disabled={patchBusy}
                                  autoFocus={priceFocusKey === `${v.shopify_variant_id}:compare`}
                                  value={variantEdits[v.shopify_variant_id]?.compare ?? ''}
                                  onChange={(e) =>
                                    setVariantEdits((prev) => ({
                                      ...prev,
                                      [v.shopify_variant_id]: {
                                        ...prev[v.shopify_variant_id],
                                        compare: e.target.value
                                      }
                                    }))
                                  }
                                  aria-label={`Compare-at for ${v.variant_title || v.sku || v.shopify_variant_id}`}
                                />
                              </div>
                            ) : (() => {
                              const row = variantEdits[v.shopify_variant_id];
                              const origCompare = v.compare_at_price != null ? String(v.compare_at_price) : '';
                              const editedRaw = row?.compare;
                              const isDirty = editedRaw != null && editedRaw !== origCompare;
                              const raw = editedRaw != null ? editedRaw : origCompare;
                              const n = raw === '' ? null : parseFloat(raw);
                              const display = n == null || !Number.isFinite(n)
                                ? (raw === '' ? '—' : raw)
                                : dollarFormat.format(n);
                              return (
                                <div
                                  className="product-analysis-table__cell product-analysis-detail__cell-display"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    setPriceFocusKey(`${v.shopify_variant_id}:compare`);
                                    setPricesEditing(true);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      setPriceFocusKey(`${v.shopify_variant_id}:compare`);
                                      setPricesEditing(true);
                                    }
                                  }}
                                  aria-label={`Edit compare-at for ${v.variant_title || v.sku || v.shopify_variant_id}`}
                                  title="Click to edit prices"
                                  style={{ cursor: 'pointer', fontWeight: isDirty ? 700 : undefined }}
                                >
                                  {display}
                                </div>
                              );
                            })()}
                            <div className="product-analysis-table__cell">{numberFormat.format(Math.round(v.qty_sold || 0))}</div>
                            <div className="product-analysis-table__cell">{dollarFormat.format(v.revenue || 0)}</div>
                            <div className="product-analysis-table__cell">
                              {numberFormat.format(Math.round((v.avg_daily_qty || 0) * 10) / 10)}
                            </div>
                            <div className="product-analysis-table__cell">
                              {numberFormat.format(variantOosDaysInWindow.get(String(v.shopify_variant_id)) || 0)}
                            </div>
                            <div className="product-analysis-table__cell">
                              {rangeRevenue > 0 ? `${Math.round((v.percent_of_product_revenue || 0) * 1000) / 10}%` : '0%'}
                            </div>
                            <div className="product-analysis-table__cell">
                              {v.estimated_sellout_date ? new Date(v.estimated_sellout_date).toLocaleDateString() : '—'}
                            </div>
                            <div className="product-analysis-table__cell">
                              {numberFormat.format(Math.round(v.inventory_current || 0))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {!loading && variants.length > 0 && (
                  <div
                    className="product-analysis-table__row"
                    style={{
                      position: 'relative',
                      background: '#111',
                      color: '#fff',
                      fontWeight: 700,
                      flexShrink: 0,
                      borderRadius: '0 0 var(--small-border-radius) var(--small-border-radius)'
                    }}
                  >
                    <div
                      className="product-analysis-table__cell"
                      style={{ display: 'flex', alignItems: 'center' }}
                    >
                      Total
                    </div>
                    <div className="product-analysis-table__metrics">
                      <div className="product-analysis-table__cell">—</div>
                      {pricesEditing ? (
                        <div className="product-analysis-table__cell product-analysis-detail__cell-input">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="product-analysis-detail__table-input"
                            disabled={patchBusy}
                            autoFocus={priceFocusKey === 'total:price'}
                            value={variantSummaryRow.price}
                            onChange={(e) => {
                              const value = e.target.value;
                              setVariantEdits((prev) => {
                                const next = { ...prev };
                                for (const v of variants) {
                                  const id = String(v.shopify_variant_id);
                                  next[id] = { ...next[id], price: value };
                                }
                                return next;
                              });
                            }}
                            aria-label="Price for all variants"
                          />
                        </div>
                      ) : (() => {
                        const raw = variantSummaryRow.price;
                        const n = raw === '' ? null : parseFloat(raw);
                        const display = raw === ''
                          ? '-'
                          : Number.isFinite(n)
                            ? dollarFormat.format(n)
                            : raw;
                        return (
                          <div
                            className="product-analysis-table__cell product-analysis-detail__cell-display"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setPriceFocusKey('total:price');
                              setPricesEditing(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPriceFocusKey('total:price');
                                setPricesEditing(true);
                              }
                            }}
                            aria-label="Edit price for all variants"
                            title="Click to edit all variant prices"
                            style={{ cursor: 'pointer' }}
                          >
                            {display}
                          </div>
                        );
                      })()}
                      {pricesEditing ? (
                        <div className="product-analysis-table__cell product-analysis-detail__cell-input">
                          <input
                            type="number"
                            min={0}
                            step="0.01"
                            className="product-analysis-detail__table-input"
                            placeholder="—"
                            disabled={patchBusy}
                            autoFocus={priceFocusKey === 'total:compare'}
                            value={variantSummaryRow.compare}
                            onChange={(e) => {
                              const value = e.target.value;
                              setVariantEdits((prev) => {
                                const next = { ...prev };
                                for (const v of variants) {
                                  const id = String(v.shopify_variant_id);
                                  next[id] = { ...next[id], compare: value };
                                }
                                return next;
                              });
                            }}
                            aria-label="Compare-at price for all variants"
                          />
                        </div>
                      ) : (() => {
                        const raw = variantSummaryRow.compare;
                        const n = raw === '' ? null : parseFloat(raw);
                        const display = raw === ''
                          ? '-'
                          : Number.isFinite(n)
                            ? dollarFormat.format(n)
                            : raw;
                        return (
                          <div
                            className="product-analysis-table__cell product-analysis-detail__cell-display"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              setPriceFocusKey('total:compare');
                              setPricesEditing(true);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setPriceFocusKey('total:compare');
                                setPricesEditing(true);
                              }
                            }}
                            aria-label="Edit compare-at price for all variants"
                            title="Click to edit all variant compare-at prices"
                            style={{ cursor: 'pointer' }}
                          >
                            {display}
                          </div>
                        );
                      })()}
                      <div className="product-analysis-table__cell">
                        {numberFormat.format(Math.round(variantSummaryRow.qty))}
                      </div>
                      <div className="product-analysis-table__cell">
                        {dollarFormat.format(variantSummaryRow.revenue || 0)}
                      </div>
                      <div className="product-analysis-table__cell">
                        {numberFormat.format(Math.round((variantSummaryRow.avgDaily || 0) * 10) / 10)}
                      </div>
                      <div className="product-analysis-table__cell">
                        {numberFormat.format(variantSummaryRow.oosDays || 0)}
                      </div>
                      <div className="product-analysis-table__cell">
                        {variantSummaryRow.percentOfProduct > 0
                          ? `${Math.round(variantSummaryRow.percentOfProduct * 1000) / 10}%`
                          : '0%'}
                      </div>
                      <div className="product-analysis-table__cell">
                        {variantSummaryRow.estimatedSelloutDate
                          ? variantSummaryRow.estimatedSelloutDate.toLocaleDateString()
                          : '—'}
                      </div>
                      <div className="product-analysis-table__cell">
                        {numberFormat.format(Math.round(variantSummaryRow.inventory || 0))}
                      </div>
                    </div>
                  </div>
                )}
                {pricesDirty && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: 8,
                      padding: '10px 0 2px',
                      marginTop: 8
                    }}
                  >
                    <button
                      type="button"
                      className="product-briefs__btn product-briefs__btn--secondary"
                      disabled={patchBusy}
                      onClick={resetVariantPrices}
                    >
                      Reset
                    </button>
                    <button
                      type="button"
                      className="product-briefs__btn product-briefs__btn--primary"
                      disabled={patchBusy}
                      onClick={commitAllVariantPrices}
                    >
                      {patchBusy ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
